import * as React from "react";
import { MultiSelect } from "../../../forms/informedv2/MultiSelect";
import { Link, useParams } from "react-router-dom";
import { AuditForm, AuditModal } from "../../../auditmodal/components/AuditModal";
import { useCurrentStudyConfigurationContext } from "../../../Core/CurrentStudyConfigurationContext";
import { SessionTypes } from "../../../misc/api/ApiSessionTypes";
import { StudyPulseOximetrySetting } from "../../../misc/api/apiTypes";
import { ParameterEntity } from "../../../misc/api/SerenityReferenceData";
import LoadingWithMessage from "../../../misc/components/LoadingWithMessage";
import { useCookieRedirect } from "../../../misc/CookieRedirect";
import Alert, { AlertCategoryEnum } from "../../../misc/components/Alert";
import { TextBox } from "../../../forms/informedv2/TextBox";
import { Form, useFormApi, Checkbox, Option, useFormState } from "informed";
import { ActionButtons } from "../../../misc/ActionButtons";
import { useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";
declare const rootUrl: string;

interface PulseOximetrySettingsScreenFormState {
    maximumRecordingTime: number;
    displayAsDuration: boolean;
    displayMedianValues: boolean;
    bpmMaximum: number;
}

interface PulseOximetrySettingsScreenFormProps {
    studyId: string;
}


export const PulseOximetrySettingsScreenForm = ({ studyId }: PulseOximetrySettingsScreenFormProps) => {
    const formApi = useFormApi<PulseOximetrySettingsScreenFormState>();
    const formState = useFormState<PulseOximetrySettingsScreenFormState>();
    const [submitting, setSubmitting] = React.useState(false);
    const [alertShown, setAlertShown] = React.useState(false);
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const [auditModalVisible, setAuditModalVisible] = React.useState(false);
    const { refreshSettings: refresh } = useCurrentStudyConfigurationContext();
    const legacyClient = useWebPortalLegacyClient();

    const onPreAuditSubmit = () => {
        setErrorAlert(null);
        setSubmitting(true);
        formApi.validate();

        const state = formApi.getState();

        if (state.invalid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        setSubmitting(false);
        setAuditModalVisible(true);
    }

    const onSubmit = async (auditForm: AuditForm) => {
        setSubmitting(true);
        setAuditModalVisible(false);
        setErrorAlert(null);

        formApi.validate();
        const state = formApi.getState();

        if (state.invalid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        const pulseOximetrySettings: StudyPulseOximetrySetting = {
            BpmMaximum: state.values.bpmMaximum,
            DisplayAsDuration: state.values.displayAsDuration,
            DisplayMedianValues: state.values.displayMedianValues,
            MaximumRecordingTime: state.values.maximumRecordingTime,
        }

        const payload = JSON.stringify(pulseOximetrySettings);

        try {
            const result = await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.PulseOximetry.StudyPulseOximetrySetting", payload);

            if (result) {
                setAlertShown(true);
                refresh();
            }
        } catch (ex) {
            setErrorAlert(ex.Message);
        }

        setSubmitting(false);
    };

    const bpmValidation = (value: number) => {
        return value >= 0 && value <= 300;
    }

    const maxRecordingTimeValidation = (value: number) => {
        return value >= 1 && value <= 20;
    }

    return (
        <>
            <Alert show={alertShown} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to="/studies">Studies list</Link>?
            </Alert>
            <Alert show={errorAlert !== null} automationid="ErrorPane" category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            <div className="row">
                <div className="col-md-12">
                    <h2 className="text-primary">Pulse Oximetry Settings</h2>
                    <div className="panel panel-default">
                        <div className="panel-heading">
                            <h3 className="panel-title">Session Configuration</h3>
                        </div>
                        <div className="panel-body">
                            <div className="row">
                                <div className="form-group col-md-6">
                                    <label>Maximum Heart Rate</label>
                                    <p className="help-block">Configures the maximum heart rate in beats per minute.</p>
                                    <TextBox<number, PulseOximetrySettingsScreenFormState> id="bpmMaximum" data-automationid="MaximumHeartRateTextbox" field="bpmMaximum" type="number" min="0" max="300" required validate={bpmValidation} />
                                </div>
                                <div className="form-group col-md-6">
                                    <label>Maximum Recording Duration</label>
                                    <p className="help-block">Configures the maximum recording duration in seconds.</p>
                                    <TextBox<number, PulseOximetrySettingsScreenFormState> id="maximumRecordingTime" data-automationid="MaximumRecordingTimeTextBox" field="maximumRecordingTime" type="number" min="1" max="20" required validate={maxRecordingTimeValidation} />
                                </div>

                            </div>

                            <div className="row">
                                <div className="form-group col-md-6">
                                    <label>Display as duration enabled</label>
                                    <p className="help-block">Configures whether display as duration is enabled.</p>
                                    <Checkbox id="displayAsDurationToggle" data-automationid="DisplayedMedianValuesCheckbox" className="checkbox" field="displayAsDuration" />
                                </div>

                                <div className="form-group col-md-6">
                                    <label>Display median values enabled</label>
                                    <p className="help-block">Configures whether median values are enabled.</p>
                                    <Checkbox id="displayAsDurationToggle" data-automationid="DisplayedAsDurationCheckbox" className="checkbox" field="displayMedianValues" />
                                </div>
                            </div>

                            <div className="row">
                                
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-12">
                            <ActionButtons submitting={submitting} onSaveClick={() => onPreAuditSubmit()} automationIdPrefix="ActionsHeader" returnToListRoute="/studies" />
                        </div>
                    </div>
                </div>
            </div>
            {auditModalVisible ? (
                <AuditModal
                    header="Approval"
                    isSubmitting={submitting}
                    onClose={() => setAuditModalVisible(false)}
                    onSubmit={(audit) => onSubmit(audit)}
                />
            ) : null}
        </>
    );
}

export const PulseOximetrySettings = () => {
    const { id } = useParams<{ id: string }>();
    const [initialValues, setInitialValues] = React.useState<PulseOximetrySettingsScreenFormState>();
    const legacyClient = useWebPortalLegacyClient();

    useCookieRedirect(id);

    React.useEffect(() => {
        const fillData = async () => {
            const hydrate = async (pulseOximetrySettings: StudyPulseOximetrySetting) => {
                const formState = {
                    displayAsDuration: pulseOximetrySettings.DisplayAsDuration,
                    displayMedianValues: pulseOximetrySettings.DisplayMedianValues,
                    maximumRecordingTime: pulseOximetrySettings.MaximumRecordingTime,
                    bpmMaximum: pulseOximetrySettings.BpmMaximum,
                }

                setInitialValues(formState);
            }

            const pulseOximetrySettings = await legacyClient.getStudyPulseOximetrySettings(id);
            hydrate(pulseOximetrySettings);
        }

        fillData();
    }, [id]);

    if (!initialValues) {
        return <LoadingWithMessage message="Loading Pulse Oximetry Settings..." />;
    }

    const validateFields = (values) => {
        return {
            maximumRecordingTime: values.maximumRecordingTime > 20 || values.maximumRecordingTime < 1 ? "Value must be between 1 and 20": undefined,
            displayAsDuration: undefined,
            displayMedianValues: undefined,
            bpmMaximum: values.bpmMaximum < 0  || values.bpmMaximum > 300? "Value must be between 0 and 300": undefined,
        }
    }

    return (
        <Form id="pulseOximetrySettingsForm" noValidate initialValues={initialValues} validateFields={validateFields}>
            <PulseOximetrySettingsScreenForm studyId={id} />
        </Form>
    );
};
